import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { ArchiveOutlined, ArrowDropDown } from '@mui/icons-material';
import { Button, useTheme } from '@mui/material';
import {
  NumberFormatPreset,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  formatNumber,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { format } from 'date-fns';
import { isEmpty } from 'ramda';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Line, LineChart, ReferenceLine } from 'recharts';

import { I18nNamespace } from '~/common/types';
import { BaseChart } from '~/components/BaseChart/BaseChart';
import { AxisConfig } from '~/components/BaseChart/BaseChart.types';
import { ButtonSelect } from '~/components/ButtonSelect/ButtonSelect';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { Dot } from '~/components/SupplierMapDetail/SupplierMapDetailContent/SupplierMapDetailContent.styled';
import { lastYearToNext6MonthsDateRange } from '~/util/cube';

import { DataConfig } from './LargeViewMaterialWidget.types';

export const LargeViewMaterialWidget = () => {
  const { entityId } = useModuleContext();
  const [widgetRef, active] = useScrollIntoView({ hash: 'large-view-material' });
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'LargeViewMaterialWidget' });
  const [option, setOption] = useState(0);
  const theme = useTheme();

  const optionItems = [
    {
      label: t('volume'),
      value: 0,
    },
    {
      label: t('quantity'),
      value: 1,
    },
  ];

  const cube = 'CommodityDeliveryHistory';

  const filters: Filter[] = [
    {
      member: `${cube}.commodity_id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const query = useCubeQuery({
    filters,
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        dateRange: lastYearToNext6MonthsDateRange,
      },
    ],
    dimensions: [`${cube}.date`, `${cube}.order_volume`, `${cube}.quantity`],
    order: {
      [`${cube}.date`]: 'asc',
    },
  });

  const chartData: DataConfig[] =
    query.resultSet?.rawData()?.map((segment) => {
      const currentDate = format(new Date(), 'yy-MM');
      const date = format(new Date(String(segment[`${cube}.date`])), 'yy-MM');
      const formattedDate = format(new Date(String(segment[`${cube}.date`])), 'MM/yyyy');
      const volumeValue = Number(segment[`${cube}.order_volume`]);
      const quantityValue = Number(segment[`${cube}.quantity`]);

      if (date < currentDate) {
        return {
          volume: volumeValue,
          quantity: quantityValue,
          formattedDate,
        };
      } else if (date === currentDate) {
        return {
          volume: volumeValue,
          quantity: quantityValue,
          futureVolume: volumeValue,
          futureQuantity: quantityValue,
          formattedDate,
        };
      } else {
        return {
          futureVolume: volumeValue,
          futureQuantity: quantityValue,
          formattedDate,
        };
      }
    }) ?? [];

  const lineProps: any = {
    strokeWidth: 3,
    isAnimationActive: false,
    dot: false,
    legendType: 'circle',
  };

  const sources = useMemo(() => {
    return [
      {
        label: t('volume'),
        tickFormatter: formatNumber(NumberFormatPreset.CURRENCY),
      },
      {
        label: t('quantity'),
        tickFormatter: formatNumber(NumberFormatPreset.INTEGER),
      },
    ];
  }, [t]);

  const yAxis: AxisConfig = useMemo(() => {
    return {
      label: sources[option].label,
      tickFormatter: sources[option].tickFormatter,
      width: 85,
      padding: {
        top: 5,
      },
    };
  }, [option, sources]);

  const currentMonth = format(new Date(), 'MM/yyyy');

  const isDataEmpty = isEmpty(chartData);

  return (
    <Widget hover="js" ref={widgetRef} active={active}>
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<ArchiveOutlined fontSize="small" />} />}
        actionsLeft={
          <ButtonSelect
            endIcon={<ArrowDropDown fontSize="small" />}
            items={optionItems}
            selected={option}
            onItemClick={setOption}
          />
        }
        actionsRight={
          <>
            <Button
              startIcon={<Dot sx={{ '&::before': { bgcolor: theme.palette.primary.main } }} />}
              endIcon={<ArrowDropDown fontSize="small" />}
              disabled
            >
              {t('deliveryLastDate', { months: 12 })}
            </Button>
            <Button
              startIcon={<Dot sx={{ '&::before': { bgcolor: theme.palette.secondary.main } }} />}
              endIcon={<ArrowDropDown fontSize="small" />}
              disabled
            >
              {t('deliveryNextDate', { months: 6 })}
            </Button>
          </>
        }
      />
      <WidgetContent>
        {!isDataEmpty ? (
          <BaseChart data={chartData} dataKey="formattedDate" chartComponent={LineChart} legend tooltip yAxis={yAxis}>
            {() => (
              <>
                <Line
                  name={option === 0 ? t('volume') : t('quantity')}
                  type="monotone"
                  dataKey={option === 0 ? 'volume' : 'quantity'}
                  stroke={theme.palette.primary.main}
                  activeDot={{ r: 6, strokeWidth: 4 }}
                  {...lineProps}
                />
                <Line
                  name={option === 0 ? t('futureVolume') : t('futureQuantity')}
                  type="monotone"
                  dataKey={option === 0 ? 'futureVolume' : 'futureQuantity'}
                  strokeDasharray="8 4"
                  stroke={theme.palette.secondary.main}
                  {...lineProps}
                />
                <ReferenceLine x={currentMonth} stroke={theme.palette.primary.main}>
                  <Label
                    value={t('datapoint') as string}
                    position="bottom"
                    fill={theme.palette.text.primary}
                    style={{ ...theme.typography.caption }}
                  />
                </ReferenceLine>
              </>
            )}
          </BaseChart>
        ) : (
          <PlaceholderWidget withImage />
        )}
      </WidgetContent>
    </Widget>
  );
};
